import React from 'react';

import {
  IAjuste,
  IContaDoPlano,
  ICruzamentos,
  IResumo,
  ISecao,
  IVerificacao,
  useFechamentoEmpresa,
  useFechamentoSecao,
} from '../../../../../../hooks/useFechamentoContabilMensal';
import { mesLongo, REGIME } from '../../regras';
import { formatarCnpj } from '../../regrasDaBusca';
import { NOMES_MES, situacaoDaTela, situacaoDoMes, ultimoMesDe } from '../../regrasDaCarteira';
import { textoDoRt } from '../../regrasDoDetalhe';
import { AbaAjustes } from './AbaAjustes';
import { AbaConstatacoes } from './AbaConstatacoes';
import { AbaCruzamentos } from './AbaCruzamentos';
import { AbaResumo } from './AbaResumo';
import { ROTULO } from './estilos';

export type AbaDoDetalhe = 'resumo' | 'constatacoes' | 'cruzamentos' | 'ajustes';
const ABAS: { chave: AbaDoDetalhe; nome: string }[] = [
  { chave: 'resumo', nome: 'Resumo' },
  { chave: 'constatacoes', nome: 'Constatações' },
  { chave: 'cruzamentos', nome: 'Cruzamentos' },
  { chave: 'ajustes', nome: 'Ajustes propostos' },
];

const ABREV = NOMES_MES.map(n => n.slice(0, 3).toLowerCase());

const mesAnterior = (competencia: string) => {
  const [a, m] = competencia.split('-').map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
};

function Aviso({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        padding: 22,
        textAlign: 'center',
        fontSize: 13,
        color: '#52525B',
        background: 'rgba(248, 250, 252, .8)',
        borderRadius: 12,
      }}
    >
      {children}
    </p>
  );
}

/** Seção que não veio: carregando, sem revisão ou versão sem detalhe. */
export function Pendente({
  secao,
  nome,
}: {
  secao: { isLoading: boolean; data?: ISecao<unknown> };
  nome: string;
}) {
  if (secao.isLoading) return <Aviso>Carregando {nome}...</Aviso>;
  if (secao.data && !secao.data.disponivel)
    return (
      <Aviso>
        Detalhe indisponível para a versão {secao.data.versao}. {secao.data.motivo}
      </Aviso>
    );
  return <Aviso>Sem {nome} para esta competência.</Aviso>;
}

/**
 * Detalhe de uma empresa marcada nos cards, na competência do card. Lê só a
 * revisão gravada: cada aba busca a sua seção quando é aberta.
 */
export function DetalheDaEmpresa({
  empresaId,
  competencia,
  razao,
  aba,
  onAba,
}: {
  empresaId: number;
  competencia: string;
  /** do card, enquanto a revisão carrega */
  razao: string;
  aba: AbaDoDetalhe;
  onAba: (a: AbaDoDetalhe) => void;
}) {
  const ano = Number(competencia.slice(0, 4));
  const hoje = new Date();
  const resumo = useFechamentoSecao<IResumo>('resumo', empresaId, competencia);
  const anterior = useFechamentoSecao<IResumo>(
    'resumo',
    empresaId,
    mesAnterior(competencia),
    aba === 'resumo',
  );
  const { data: doAno } = useFechamentoEmpresa(empresaId, `${ano}-01`, `${ano}-12`);
  const verificacoes = useFechamentoSecao<IVerificacao[]>(
    'verificacoes',
    empresaId,
    competencia,
    aba === 'constatacoes',
  );
  const plano = useFechamentoSecao<Record<string, IContaDoPlano>>(
    'plano',
    empresaId,
    competencia,
    aba === 'constatacoes' || aba === 'cruzamentos',
  );
  // a contagem da aba Cruzamentos é a dos divergentes: a seção vem com o detalhe
  const cruzamentos = useFechamentoSecao<ICruzamentos>('cruzamentos', empresaId, competencia);
  const ajustes = useFechamentoSecao<IAjuste[]>(
    'ajustes',
    empresaId,
    competencia,
    aba === 'ajustes',
  );

  const r = resumo.data?.dados;
  const empresa = r?.empresa;
  const linha = doAno?.empresa;
  const regime = empresa?.regime_texto ?? REGIME[empresa?.regime ?? ''];
  const info = [
    linha?.external_id,
    empresa?.cnpj ? `CNPJ ${formatarCnpj(empresa.cnpj)}` : null,
    regime,
    mesLongo(competencia),
    resumo.data ? `versão ${resumo.data.versao}` : null,
    textoDoRt(empresa?.rt),
  ]
    .filter(Boolean)
    .join(' · ');

  const ultimo = ultimoMesDe(ano, hoje);
  const porMes = new Map((linha?.meses ?? []).map(m => [m.competencia, m]));
  const divergentes = (cruzamentos.data?.dados?.cruzamentos ?? []).filter(
    c => c.situacao === 'divergente',
  ).length;
  const contagem: Partial<Record<AbaDoDetalhe, number>> = {
    constatacoes: r ? r.contagem.critica + r.contagem.alta : 0,
    cruzamentos: divergentes,
    ajustes: r?.ajustes ?? 0,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ ...ROTULO, letterSpacing: '.08em' }}>Fechamento contábil mensal</div>
        <h2
          style={{
            margin: '2px 0',
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: '-.01em',
            lineHeight: 1.15,
            textTransform: 'uppercase',
          }}
        >
          {empresa?.razao ?? razao}
        </h2>
        <div style={{ fontSize: 12, color: '#52525B' }}>{info}</div>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '10px 12px',
          padding: '10px 14px',
          borderRadius: 12,
          border: '1px solid #E4E4E7',
          background: 'rgba(255, 255, 255, .6)',
        }}
      >
        <span style={ROTULO}>Competência</span>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{ano}</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {ABREV.slice(0, Math.max(0, ultimo + 1)).map((rot, i) => {
            const comp = `${ano}-${String(i + 1).padStart(2, '0')}`;
            const m = porMes.get(comp);
            const s = m?.tem_detalhe ? situacaoDaTela(situacaoDoMes(m)) : null;
            return (
              <span
                key={comp}
                title={`${NOMES_MES[i]} de ${ano}: ${s ? s.curto.toLowerCase() : 'sem revisão'}`}
                style={{
                  minWidth: 40,
                  boxSizing: 'border-box',
                  padding: '5px 8px',
                  borderRadius: 8,
                  textAlign: 'center',
                  fontSize: 11.5,
                  fontWeight: 700,
                  background: s?.suave ?? '#F1F5F9',
                  color: s?.texto ?? '#64748B',
                  boxShadow: comp === competencia ? '0 0 0 2px #1B6FD0' : 'none',
                }}
              >
                {rot}
              </span>
            );
          })}
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Seções da empresa"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 2, borderBottom: '1px solid #E4E4E7' }}
      >
        {ABAS.map(a => {
          const ativo = a.chave === aba;
          const n = contagem[a.chave] ?? 0;
          return (
            <button
              type="button"
              key={a.chave}
              role="tab"
              aria-selected={ativo}
              onClick={() => onAba(a.chave)}
              style={{
                padding: '10px 12px',
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: ativo ? 700 : 500,
                color: ativo ? '#0F172A' : '#52525B',
                boxShadow: `inset 0 -2px 0 ${ativo ? '#1B6FD0' : 'transparent'}`,
              }}
            >
              {a.nome}
              {n ? ` (${n})` : ''}
            </button>
          );
        })}
      </div>

      {aba === 'resumo' &&
        (r && resumo.data?.disponivel ? (
          <AbaResumo
            resumo={r}
            anterior={anterior.data?.disponivel ? anterior.data.dados : undefined}
            competencia={competencia}
            onVerTodas={() => onAba('constatacoes')}
          />
        ) : (
          <Pendente secao={resumo} nome="a revisão" />
        ))}
      {aba === 'constatacoes' &&
        (verificacoes.data?.dados ? (
          <AbaConstatacoes itens={verificacoes.data.dados} plano={plano.data?.dados} />
        ) : (
          <Pendente secao={verificacoes} nome="constatações" />
        ))}
      {aba === 'cruzamentos' &&
        (cruzamentos.data?.dados ? (
          <AbaCruzamentos
            dados={cruzamentos.data.dados}
            materialidade={r?.materialidade}
            plano={plano.data?.dados}
          />
        ) : (
          <Pendente secao={cruzamentos} nome="cruzamentos" />
        ))}
      {aba === 'ajustes' &&
        (ajustes.data?.dados ? (
          <AbaAjustes ajustes={ajustes.data.dados} lucroDoMes={r?.dre.mes.lucro_liquido} />
        ) : (
          <Pendente secao={ajustes} nome="ajustes" />
        ))}
    </div>
  );
}
